import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for horizontal scroll pagination in tables.
 * Provides state and handlers for navigating horizontally scrollable content.
 *
 * @param {React.RefObject} containerRef - Ref to the scrollable container
 * @param {Array} dependencies - Array of dependencies to trigger recalculation
 * @returns {Object} Horizontal scroll state and handlers
 */
const useHorizontalScroll = (containerRef, dependencies = []) => {
  const [horizontalPages, setHorizontalPages] = useState(1);
  const [currentHorizontalPage, setCurrentHorizontalPage] = useState(0);
  const [showHorizontalPager, setShowHorizontalPager] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Update horizontal pagination based on scroll position
  const updateHorizontalPagination = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const scrollWidth = container.scrollWidth;
    const scrollLeft = container.scrollLeft;

    // Check if horizontal scrolling is needed
    const needsScrolling = scrollWidth > containerWidth;
    setShowHorizontalPager(needsScrolling);

    if (needsScrolling) {
      // Calculate number of pages (each page shows ~80% of container width for overlap)
      const pageWidth = containerWidth * 0.8;
      const pages = Math.ceil(scrollWidth / pageWidth);
      setHorizontalPages(pages);

      // Calculate current page
      const currentPage = Math.ceil(scrollLeft / pageWidth);
      setCurrentHorizontalPage(currentPage);

      // Check if we can scroll further right (with 5px tolerance)
      const maxScrollLeft = scrollWidth - containerWidth;
      const canScroll = scrollLeft < maxScrollLeft - 5;
      setCanScrollRight(canScroll);
    } else {
      setHorizontalPages(1);
      setCurrentHorizontalPage(0);
      setCanScrollRight(false);
    }
  }, [containerRef]);

  // Set up scroll and resize listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial calculation
    updateHorizontalPagination();

    // Add scroll listener
    container.addEventListener('scroll', updateHorizontalPagination);

    // Add resize observer
    const resizeObserver = new ResizeObserver(updateHorizontalPagination);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateHorizontalPagination);
      resizeObserver.disconnect();
    };
  }, [updateHorizontalPagination, ...dependencies]);

  // Navigate to specific page
  const scrollToHorizontalPage = useCallback((pageIndex) => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const pageWidth = containerWidth * 0.8;
    const scrollLeft = pageIndex * pageWidth;

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }, [containerRef]);

  // Navigate to next page
  const handleArrowClick = useCallback(() => {
    const nextPage = currentHorizontalPage + 1;
    if (nextPage < horizontalPages) {
      scrollToHorizontalPage(nextPage);
    }
  }, [currentHorizontalPage, horizontalPages, scrollToHorizontalPage]);

  return {
    horizontalPages,
    currentHorizontalPage,
    showHorizontalPager,
    setShowHorizontalPager,
    canScrollRight,
    scrollToHorizontalPage,
    handleArrowClick
  };
};

export default useHorizontalScroll;
